import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
// mismas tarjetas, formulario y lista que Catálogos y Usuarios
import style from "../../scss/Catalogos.module.scss";
import estiloUsuarios from "../../scss/Usuarios.module.scss";
import BotonIconoComponent, { AccionesTabla, BotonEditar } from "../../components/BotonIconoComponent";
import { crearAdministrador, editarAdministrador, getAdministradores } from "../../Api/ApiAdministradores";
import { obtenerMensajeError } from "../../utils/mensajeError";
import { estadoFechaCorte, formatearFechaCorte, formatearFechaRegistro } from "../../utils/fechaCorte";

const FORMULARIO_VACIO = { username: "", password: "", correo: "", fechaCorte: "", dobleFactor: true };

// Superadmin: administradores (candidatos). Cada uno es una cuenta con sus
// digitadores; aquí no se ven sus electores, solo cuántos tiene.
const AdministradoresPages = () => {
  const [administradores, setAdministradores] = useState([]);
  const [formulario, setFormulario] = useState(FORMULARIO_VACIO);
  const [editando, setEditando] = useState(null); // administrador que se está editando
  const [guardando, setGuardando] = useState(false);

  const cargarAdministradores = useCallback(async () => {
    try {
      setAdministradores(await getAdministradores());
    } catch (error) {
      Swal.fire({ icon: "error", text: obtenerMensajeError(error) });
    }
  }, []);

  useEffect(() => {
    cargarAdministradores();
  }, [cargarAdministradores]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormulario({ ...formulario, [name]: type === "checkbox" ? checked : value });
  };

  const limpiarFormulario = () => {
    setFormulario(FORMULARIO_VACIO);
    setEditando(null);
  };

  const iniciarEdicion = (administrador) => {
    setEditando(administrador);
    setFormulario({
      username: administrador.username,
      password: "",
      correo: administrador.correo ?? "",
      fechaCorte: administrador.fechaCorte ?? "",
      dobleFactor: administrador.dobleFactor,
    });
  };

  const notificarExito = (mensaje) =>
    Swal.fire({ icon: "success", text: mensaje, timer: 1800, showConfirmButton: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const datos = { ...formulario, username: formulario.username.trim() };
      const respuesta = editando ? await editarAdministrador(editando.id, datos) : await crearAdministrador(datos);
      notificarExito(respuesta.msg);
      limpiarFormulario();
      cargarAdministradores();
    } catch (error) {
      Swal.fire({ icon: "error", text: obtenerMensajeError(error) });
    } finally {
      setGuardando(false);
    }
  };

  const cambiarEstado = (administrador) => {
    const accion = administrador.estado ? "desactivar" : "activar";
    Swal.fire({
      title: `¿Seguro que deseas ${accion} a ${administrador.username}?`,
      text: administrador.estado
        ? "Ni el administrador ni sus digitadores podrán iniciar sesión. Sus datos se conservan."
        : "El administrador y sus digitadores podrán volver a iniciar sesión.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, Confirmar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const respuesta = await editarAdministrador(administrador.id, {
          correo: administrador.correo,
          fechaCorte: administrador.fechaCorte,
          estado: !administrador.estado,
        });
        notificarExito(respuesta.msg);
        cargarAdministradores();
      } catch (error) {
        Swal.fire({ icon: "error", text: obtenerMensajeError(error) });
      }
    });
  };

  return (
    <div className={`container ${style.catalogos}`}>
      <h1 className={style.encabezado}>Administradores</h1>
      <p className={style.descripcion}>
        Crea los administradores (candidatos). Cada uno maneja su propia información y sus digitadores. Al pasar la
        fecha de corte, el administrador y sus digitadores ya no pueden ingresar (sus datos se conservan).
      </p>

      <div className="row g-4">
        <div className="col-12 col-lg-4">
          <section className={style.tarjeta}>
            <h2 className={style.titulo}>{editando ? `Editar ${editando.username}` : "Nuevo administrador"}</h2>

            <form onSubmit={handleSubmit} className="row g-2">
              <div className="col-12">
                <label htmlFor="administrador-username" className={style.etiqueta}>
                  Usuario
                </label>
                <input
                  id="administrador-username"
                  name="username"
                  className="form-control"
                  value={formulario.username}
                  onChange={handleChange}
                  minLength={4}
                  maxLength={100}
                  autoComplete="off"
                  required
                />
              </div>
              <div className="col-12">
                <label htmlFor="administrador-correo" className={style.etiqueta}>
                  Correo {formulario.dobleFactor ? "(recibe el código de verificación)" : "(opcional)"}
                </label>
                <input
                  id="administrador-correo"
                  name="correo"
                  type="email"
                  className="form-control"
                  value={formulario.correo}
                  onChange={handleChange}
                  required={formulario.dobleFactor}
                />
              </div>
              <div className="col-12">
                <label htmlFor="administrador-password" className={style.etiqueta}>
                  {editando ? "Nueva contraseña (opcional)" : "Contraseña"}
                </label>
                <input
                  id="administrador-password"
                  name="password"
                  type="password"
                  className="form-control"
                  value={formulario.password}
                  onChange={handleChange}
                  minLength={8}
                  placeholder={editando ? "Déjala vacía para no cambiarla" : "Mínimo 8 caracteres"}
                  autoComplete="new-password"
                  required={!editando}
                />
              </div>
              <div className="col-12">
                <label htmlFor="administrador-fecha-corte" className={style.etiqueta}>
                  Fecha de corte (último día de acceso)
                </label>
                <input
                  id="administrador-fecha-corte"
                  name="fechaCorte"
                  type="date"
                  className="form-control"
                  value={formulario.fechaCorte}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-12">
                <div className={`form-check form-switch ${estiloUsuarios.interruptor}`}>
                  <input
                    id="administrador-doble-factor"
                    name="dobleFactor"
                    type="checkbox"
                    role="switch"
                    className="form-check-input"
                    checked={formulario.dobleFactor}
                    onChange={handleChange}
                  />
                  <label htmlFor="administrador-doble-factor" className="form-check-label">
                    Verificación por correo (2FA) al iniciar sesión
                  </label>
                </div>
              </div>

              <div className={`col-12 ${style.botones}`}>
                <button type="submit" className={`btn btn-primary ${style.boton}`} disabled={guardando}>
                  {editando ? "Guardar cambios" : "Crear administrador"}
                </button>
                {editando && (
                  <button type="button" className={`btn btn-outline-secondary ${style.boton}`} onClick={limpiarFormulario}>
                    Cancelar
                  </button>
                )}
              </div>
            </form>
          </section>
        </div>

        <div className="col-12 col-lg-8">
          <section className={style.tarjeta}>
            <h2 className={style.titulo}>
              Administradores registrados <span className={style.contador}>{administradores.length}</span>
            </h2>

            <div className={`table-responsive ${style.lista} ${estiloUsuarios.listaUsuarios}`}>
              <table className="table table-hover table-striped align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Administrador</th>
                    <th scope="col">Registrado</th>
                    <th scope="col">Fecha de corte</th>
                    <th scope="col">Estado</th>
                    <th scope="col" className="d-none d-md-table-cell text-center">Digitadores</th>
                    <th scope="col" className="d-none d-md-table-cell text-center">Electores</th>
                    <th scope="col" className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {administradores.map((administrador) => {
                    const corte = estadoFechaCorte(administrador.fechaCorte, administrador.vencido);
                    return (
                      <tr key={administrador.id} className={editando?.id === administrador.id ? "table-primary" : ""}>
                        <td>
                          {administrador.username}
                          <span className={estiloUsuarios.correoMovil}>{administrador.correo || "Sin correo"}</span>
                        </td>
                        <td className="text-nowrap">{formatearFechaRegistro(administrador.createdAt)}</td>
                        <td>
                          <span className="text-nowrap">{formatearFechaCorte(administrador.fechaCorte)}</span>
                          <span className={`badge d-table mt-1 ${corte.clase}`}>{corte.texto}</span>
                        </td>
                        <td>
                          <span className={`badge ${administrador.estado ? "bg-success" : "bg-secondary"}`}>
                            {administrador.estado ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td className="d-none d-md-table-cell text-center">{administrador.totalDigitadores}</td>
                        <td className="d-none d-md-table-cell text-center">{administrador.totalElectores}</td>
                        <td>
                          <AccionesTabla>
                            <BotonEditar
                              etiqueta={`Editar a ${administrador.username}`}
                              onClick={() => iniciarEdicion(administrador)}
                            />
                            <BotonIconoComponent
                              icono={administrador.estado ? "mdi:account-off-outline" : "mdi:account-check-outline"}
                              etiqueta={`${administrador.estado ? "Desactivar" : "Activar"} a ${administrador.username}`}
                              variante={administrador.estado ? "neutro" : "exito"}
                              onClick={() => cambiarEstado(administrador)}
                            />
                          </AccionesTabla>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AdministradoresPages;
